import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Book, Wand2, Heart, X, ChevronRight, ChevronLeft } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface WelcomeProps {
  onComplete: () => void;
}

const welcomeSteps = [
  {
    title: (
      <>
        Welcome to <span className='text-snuggle-gradient'>SnuggleCrafters.com</span>!
      </>
    ),
    description: "Create magical bedtime stories personalized just for your little one",
    icon: () => <img src="/logo.png" alt="SnuggleCrafters Logo" className="w-8 h-8 object-contain" />,
    tips: [
      "Every story is unique and tailored to your child",
      "Choose from different styles and themes",
      "Stories take about 30 seconds to create"
    ]
  },
  {
    title: "Tell Us About Your Child",
    description: "Help us create the perfect story by sharing a few details",
    icon: Heart,
    tips: [
      "Enter your child's age (2-12 years)",
      "Select their interests and favorite themes",
      "Choose a lesson or value to include"
    ]
  },
  {
    title: "Listen Along Together",
    description: "Enjoy professional narration with interactive features",
    icon: Book,
    tips: [
      "Click the play button to hear the story",
      "Words highlight as they're read aloud",
      "Toggle sound effects for extra magic"
    ]
  },
  {
    title: "Save & Share Your Stories",
    description: "Build a library of bedtime favorites",
    icon: Wand2,
    tips: [
      "All stories are saved automatically",
      "Mark favorites with the heart button",
      "Create merchandise from your stories"
    ]
  }
];

export function Welcome({ onComplete }: WelcomeProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user has seen welcome before
    const hasSeenWelcome = localStorage.getItem("hasSeenWelcome");
    if (hasSeenWelcome) {
      onComplete();
    } else {
      setIsVisible(true);
    }
  }, [onComplete]);

  const handleComplete = () => {
    localStorage.setItem("hasSeenWelcome", "true");
    setIsVisible(false);
    setTimeout(onComplete, 300);
  };

  const handleNext = () => {
    if (currentStep < welcomeSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (!isVisible) return null;

  const step = welcomeSteps[currentStep];
  const Icon = step.icon;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-white p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-title"
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="max-w-md w-full shadow-2xl">
            <CardHeader className="relative">
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-4 top-4"
                onClick={handleComplete}
                aria-label="Skip welcome tour"
              >
                <X className="h-4 w-4" />
              </Button>
              <div className="flex items-center justify-center mb-4">
                <div className="p-3 bg-gradient-to-br from-purple-500 to-teal-500 rounded-full">
                  <Icon className="h-8 w-8 text-white" />
                </div>
              </div>
              <CardTitle id="welcome-title" className="text-2xl text-center">
                {step.title}
              </CardTitle>
              <CardDescription className="text-center text-base mt-2">
                {step.description}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 mb-6" role="list">
                {step.tips.map((tip, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="mt-1 h-2 w-2 rounded-full bg-purple-500 flex-shrink-0" aria-hidden="true" />
                    <span className="text-sm text-gray-600">{tip}</span>
                  </li>
                ))}
              </ul>

              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={currentStep === 0}
                  aria-label="Previous step"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>

                <div className="flex gap-1.5" role="tablist" aria-label="Welcome steps">
                  {welcomeSteps.map((_, index) => (
                    <button
                      key={index}
                      className={`h-2 w-2 rounded-full transition-all ${
                        index === currentStep 
                          ? "bg-purple-600 w-6" 
                          : "bg-gray-300 hover:bg-gray-400"
                      }`}
                      onClick={() => setCurrentStep(index)}
                      role="tab"
                      aria-selected={index === currentStep}
                      aria-label={`Go to step ${index + 1}`}
                    />
                  ))}
                </div>

                <Button
                  variant="gradient"
                  onClick={handleNext}
                  aria-label={currentStep === welcomeSteps.length - 1 ? "Get started" : "Next step"}
                >
                  {currentStep === welcomeSteps.length - 1 ? "Get Started" : "Next"}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}